<template>
  <div>
    <div class="pr-2" v-if="$router.currentRoute.name !== 'routes'">
      <v-breadcrumbs :items="breadcrumbs">
        <template v-slot:divider>
          <v-icon>chevron_right</v-icon>
        </template>
      </v-breadcrumbs>
    </div>
    <div v-if="$router.currentRoute.name === 'routes'">
      <br />
      <h1 class="text-xs-center">Routes</h1>
      <div class="layout justify-space-between">
        <div>
          <v-btn flat :disabled="apiInProgress" :to="{ name: 'routes-events' }">
            <v-icon left>fal fa-calendar-star</v-icon> Events
          </v-btn>
        </div>
        <div class="text-xs-right">
          <v-btn class="primary" :disabled="apiInProgress || isOnlyInvestor" @click="loadRoute('0')">
            <v-icon left>fal fa-plus</v-icon> Create Route
          </v-btn>
        </div>
      </div>
      <v-layout row wrap>
        <v-flex md5 sm3 pa-2>
          <form @submit.prevent="() => searchRoutes(selectedRouteTab, 1)">
            <v-text-field
              solo
              v-model="searchKeywords"
              append-icon="fal fa-search"
              :rules="[v => !v || v.length >= 2 || 'Min 2 characters']"
              :disabled="apiInProgress"
              type="search"
              placeholder="Search route “label”"
              @click:append="() => searchRoutes(selectedRouteTab, 1)"
            />
          </form>
        </v-flex>
        <v-flex md4 sm4 pa-2>
          <v-layout>
            <v-flex xs6>
              <v-menu
                v-model="dateFromMenu"
                :close-on-content-click="false"
                full-width
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    prepend-icon="fal fa-calendar"
                    :value="computedDateFromFormatted"
                    @click:clear="dateFrom = null"
                    clearable
                    label="Updated From"
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  no-title
                  :max="computedDateFromMax"
                  v-model="dateFrom"
                  @change="dateFromMenu = false"
                />
              </v-menu>
            </v-flex>
            <v-flex xs6 pl-2>
              <v-menu
                v-model="dateToMenu"
                :close-on-content-click="false"
                full-width
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    prepend-icon="fal fa-calendar"
                    :value="computedDateToFormatted"
                    @click:clear="dateTo = null"
                    clearable
                    label="To"
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  no-title
                  :min="computedDateToMin"
                  v-model="dateTo"
                  @change="dateToMenu = false"
                />
              </v-menu>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex md2 sm3 pa-2>
          <v-select
            dense
            :menu-props="{overflowX: true}"
            :items="agencies"
            label="Agency ID"
            :value="selectedAgencyId"
            @change="onFilterByAgency"
          />
        </v-flex>
        <v-flex md1 sm2 pa-2>
          <v-btn small fab @click.stop="searchRoutes(selectedRouteTab, 1)">
            <v-icon>fal fa-filter</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
      <v-tabs icons-and-text v-model="selectedRouteTab" @change="onTabChanged">
        <v-tabs-slider color="primary" />
        <v-tab ripple href="#routes">
          Routes {{selectedRouteTab !== 'routes' ? `(${totalRoutes})` : ''}}
          <v-icon>fal fa-route</v-icon>
        </v-tab>
        <v-tab ripple href="#archived-routes">
          Archived Routes {{selectedRouteTab !== 'archived-routes' ? `(${totalArchivedRoutes})` : ''}}
          <v-icon>fal fa-archive</v-icon>
        </v-tab>
        <v-tabs-items v-model="selectedRouteTab">
          <v-tab-item value="routes">
            <v-data-table
              v-if="selectedRouteTab === 'routes'"
              :headers="activeHeaders"
              :items="routes"
              :pagination.sync="pagination"
              :total-items="totalRoutes"
              :loading="apiInProgress"
              :rows-per-page-items="[10, 25, 50]"
              @update:pagination="onPagination"
              class="elevation-1"
              must-sort
            >
              <template v-slot:items="props">
                <tr class="tr--pointer" @click="loadRoute(props.item.id)">
                  <td>{{ props.item.label }}</td>
                  <td :title="props.item.routeIdList">
                    <v-text-field
                      class="text-xs-right text-field--flat"
                      :value="props.item.routeIdList"
                      readonly
                      hide-details
                    />
                  </td>
                  <td>{{ props.item.agencyLabel }}</td>
                  <td>{{ props.item.dateCreatedLabel }}</td>
                  <td>{{ props.item.dateUpdatedLabel }}</td>
                  <td class="justify-space-between layout px-0" style="min-width: 110px">
                    <v-btn
                      flat
                      icon
                      title="View route"
                      :disabled="apiInProgress"
                      @click.stop="loadRoute(props.item.id)">
                      <v-icon small>
                        fas fa-pencil
                      </v-icon>
                    </v-btn>
                    <v-btn
                      flat
                      icon
                      title="Archive route"
                      :disabled="apiInProgress || !hasUserType('admin')"
                      @click.stop="archiveRoute(props.item)">
                      <v-icon small>
                        fas fa-archive
                      </v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-tab-item>

          <v-tab-item value="archived-routes">
            <v-data-table
              v-if="selectedRouteTab === 'archived-routes'"
              :headers="archivedHeaders"
              :items="routes"
              :pagination.sync="pagination"
              :total-items="totalArchivedRoutes"
              :loading="apiInProgress"
              :rows-per-page-items="[10, 25, 50]"
              @update:pagination="onPagination"
              class="elevation-1"
              must-sort
            >
              <template v-slot:items="props">
                <tr class="tr--pointer" @click="loadRoute(props.item.id)">
                  <td>{{ props.item.label }}</td>
                  <td :title="props.item.routeIdList">
                    <v-text-field
                      class="text-xs-right text-field--flat"
                      :value="props.item.routeIdList"
                      readonly
                      hide-details
                    />
                  </td>
                  <td>{{ props.item.agencyLabel }}</td>
                  <td>{{ props.item.dateCreatedLabel }}</td>
                  <td>{{ props.item.dateArchivedLabel }}</td>
                  <td class="justify-space-between layout px-0" style="min-width: 110px">
                    <v-btn
                      flat
                      icon
                      title="View route"
                      :disabled="apiInProgress"
                      @click.stop="loadRoute(props.item.id)">
                      <v-icon small>
                        fas fa-pencil
                      </v-icon>
                    </v-btn>
                    <v-btn
                      flat
                      icon
                      title="Delete route permanently"
                      :disabled="apiInProgress || !hasUserType('admin')"
                      @click.stop="deleteRoute(props.item)">
                      <v-icon small class="error--text">
                        fas fa-trash-alt
                      </v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </div>
    <router-view
      :force-root-view-refresh="forceRootViewRefresh"
      :signed-in-user="signedInUser"
      :set-route="setRoute"
      :has-user-type="hasUserType">
    </router-view>
  </div>
</template>

<script>
import src from './src'
export default src
</script>
